import React, {
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
  useCallback,
} from 'react'
import {
  PTDraftSVG,
  PTSentSVG,
  PTAcceptedSVG,
  PTCrossCircleSVG,
  PTCalender2SVG,
  PTLogoSVG,
  PTCompletedSVG,
} from '../../../../assets'
import {
  StatsRow,
  SliderBg,
  StatCard,
  StatIconWrap,
  StatBody,
  StatCount,
  StatLabel,
} from './styled.components'

const TILES = [
  {key: 'all', label: 'Total Proposals', bg: '#242765', Icon: PTLogoSVG},
  {key: 'draft', label: 'Draft', bg: '#0327A9', Icon: PTDraftSVG},
  {key: 'sent', label: 'Sent', bg: '#FFBD00', Icon: PTSentSVG},
  {key: 'accepted', label: 'Viewed', bg: '#009075', Icon: PTAcceptedSVG},
  {key: 'completed', label: 'Completed', bg: '#2E7D32', Icon: PTCompletedSVG},
  {key: 'expired', label: 'Expired', bg: '#EF5900', Icon: PTCalender2SVG},
  {key: 'declined', label: 'Declined', bg: '#FF0054', Icon: PTCrossCircleSVG},
]

function pad(n) {
  return String(n).padStart(2, '0')
}

function ProposalStats({counts, activeTab, onTabChange}) {
  const rowRef = useRef(null)
  const sliderRef = useRef(null)
  const animFrameRef = useRef(null)
  const targetLeftRef = useRef(0)
  const [sliderStyle, setSliderStyle] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  })

  // Recalculate the overlay position from the DOM.
  // animate=true keeps the CSS transition; animate=false snaps instantly
  // (used on resize so the overlay doesn't glide from the stale position).
  const snapToActive = useCallback(
    (animate = true) => {
      if (!rowRef.current) return
      const activeEl = rowRef.current.querySelector(
        `[data-tile="${activeTab}"]`,
      )
      if (!activeEl) return

      const rowRect = rowRef.current.getBoundingClientRect()
      const tileRect = activeEl.getBoundingClientRect()
      const newLeft = tileRect.left - rowRect.left
      const newTop = tileRect.top - rowRect.top

      targetLeftRef.current = newLeft

      const next = {
        left: newLeft,
        top: newTop,
        width: tileRect.width,
        height: tileRect.height,
      }

      if (!animate && sliderRef.current) {
        // Suppress transition so the overlay snaps directly to the new position
        sliderRef.current.style.transition = 'none'
        setSliderStyle(next)
        requestAnimationFrame(() => {
          if (sliderRef.current) sliderRef.current.style.transition = ''
        })
      } else {
        setSliderStyle(next)
      }
    },
    [activeTab],
  )

  // Tab-change: animate the overlay and track its color during the transition
  useLayoutEffect(() => {
    const activeTile = TILES.find(t => t.key === activeTab) || TILES[0]

    snapToActive(true)

    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)

    const trackColor = () => {
      if (!sliderRef.current || !rowRef.current) return

      const sliderRect = sliderRef.current.getBoundingClientRect()
      const currentRowRect = rowRef.current.getBoundingClientRect()
      const sliderCenter = sliderRect.left + sliderRect.width / 2

      let closest = activeTile
      let minDist = Infinity
      TILES.forEach(tile => {
        const el = rowRef.current.querySelector(`[data-tile="${tile.key}"]`)
        if (!el) return
        const r = el.getBoundingClientRect()
        const tileCenterX = r.left + r.width / 2
        const dist = Math.abs(tileCenterX - sliderCenter)
        if (dist < minDist) {
          minDist = dist
          closest = tile
        }
      })

      sliderRef.current.style.background = `linear-gradient(210deg, ${closest.bg} 0%, ${closest.bg}dd 100%)`

      const currentLeft = sliderRect.left - currentRowRect.left
      if (Math.abs(currentLeft - targetLeftRef.current) > 2) {
        animFrameRef.current = requestAnimationFrame(trackColor)
      }
    }

    animFrameRef.current = requestAnimationFrame(trackColor)

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
    }
  }, [activeTab, snapToActive])

  // Resize: snap the overlay instantly whenever the row's layout changes
  useEffect(() => {
    if (!rowRef.current) return
    const ro = new ResizeObserver(() => snapToActive(false))
    ro.observe(rowRef.current)
    return () => ro.disconnect()
  }, [snapToActive])

  return (
    <StatsRow ref={rowRef}>
      <SliderBg
        ref={sliderRef}
        bg={(TILES.find(t => t.key === activeTab) || TILES[0]).bg}
        style={sliderStyle}
      />
      {TILES.map(tile => {
        const selected = activeTab === tile.key
        return (
          <StatCard
            key={tile.key}
            data-tile={tile.key}
            selected={selected}
            onClick={() => onTabChange && onTabChange(tile.key)}
            bg={tile.bg}
          >
            <StatIconWrap bg={tile.bg}>
              {tile?.Icon && <tile.Icon width="22" height="22" />}
            </StatIconWrap>
            <StatBody>
              <StatLabel selected={selected}>{tile.label}</StatLabel>
              <StatCount selected={selected}>
                {pad(counts[tile.key] || 0)}
              </StatCount>
            </StatBody>
          </StatCard>
        )
      })}
    </StatsRow>
  )
}

export {ProposalStats}
